import axios from "axios";
import { API_URL, getAuthHeader } from ".";
import { ResponseModel } from "../Models/ResponseModel";

export type AwardVotingPeriod = {
  id: number;
  year: number;
  title: string;
  startsUtc: string;
  endsUtc: string;
  isPublished: boolean;
  showWinnersToMembers: boolean;
  subscriptionId?: number | null;
  subscriptionIds?: number[];
  subscriptionName?: string | null;
  voteCount: number;
  createdUtc: string;
  isOpen: boolean;
};

export type AwardCandidate = {
  userId: number;
  name: string;
  memberId: number;
  memberName: string;
  logoUrl?: string | null;
  subscriptionName?: string | null;
};

export type AwardMyVote = {
  userId: number;
  name: string;
  memberId: number;
  memberName: string;
};

export type AwardWinner = {
  userId: number;
  name: string;
  memberId: number;
  memberName: string;
  logoUrl?: string | null;
  voteCount: number;
};

export type AwardCurrentBallot = {
  period?: AwardVotingPeriod | null;
  openPeriods: AwardVotingPeriod[];
  candidates: AwardCandidate[];
  myVote?: AwardMyVote | null;
  winners?: AwardWinner[] | null;
  canVote: boolean;
  hasVoted: boolean;
  message?: string | null;
};

export type AwardTallyRow = {
  userId: number;
  name: string;
  memberId: number;
  memberName: string;
  logoUrl?: string | null;
  voteCount: number;
};

export type AwardResults = {
  period: AwardVotingPeriod;
  tallies: AwardTallyRow[];
  totalVoters: number;
};

class AwardVotingApi {
  static getCurrent() {
    return axios.get<AwardCurrentBallot>(
      `${API_URL}/api/awards/current`,
      getAuthHeader()
    );
  }

  static getPending() {
    return axios.get<AwardVotingPeriod[]>(
      `${API_URL}/api/awards/pending`,
      getAuthHeader()
    );
  }

  static getOpenPeriods() {
    return axios.get<AwardVotingPeriod[]>(
      `${API_URL}/api/awards/open-periods`,
      getAuthHeader()
    );
  }

  static getBallot(periodId: number) {
    return axios.get<AwardCurrentBallot>(
      `${API_URL}/api/awards/ballot/${periodId}`,
      getAuthHeader()
    );
  }

  /** Lightweight check for SideNav visibility. */
  static isVotingOpen() {
    return axios.get<{ isOpen: boolean }>(
      `${API_URL}/api/awards/open`,
      getAuthHeader()
    );
  }

  static castVote(votingPeriodId: number, userId: number) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/awards/vote`,
      { votingPeriodId, userId },
      getAuthHeader()
    );
  }

  static getPeriods() {
    return axios.get<AwardVotingPeriod[]>(
      `${API_URL}/api/awards/admin/periods`,
      getAuthHeader()
    );
  }

  static upsertPeriod(body: {
    id: number;
    year: number;
    title: string;
    startsUtc: string;
    endsUtc: string;
    isPublished: boolean;
    showWinnersToMembers: boolean;
    subscriptionId: number;
    subscriptionIds: number[];
  }) {
    return axios.post<ResponseModel>(
      `${API_URL}/api/awards/admin/periods`,
      body,
      getAuthHeader()
    );
  }

  static getResults(periodId: number) {
    return axios.get<AwardResults>(
      `${API_URL}/api/awards/admin/results/${periodId}`,
      getAuthHeader()
    );
  }
}

export default AwardVotingApi;
